'use client'

import type { Options } from 'highcharts'

import { formatters } from '@morpho-org/ui-v6/utils'
import { memo, useMemo } from 'react'

import type { ChartBaseProps } from '../types/chart'

import { createTooltipFormatter, mergeOptions } from '../helpers/highcharts'
import { useChartTheme } from '../hooks/useChartTheme'
import { useHighchartsOptions } from '../hooks/useHighchartsOptions'
import { Chart } from './Chart'

export interface SimpleChartProps extends ChartBaseProps {
  noAnimation?: boolean
  borderRadius?: NonNullable<
    ReturnType<typeof useHighchartsOptions>['options']['chart']
  >['borderRadius']
  flexGrow?: number
  hideXAxis?: boolean
  hideYAxis?: boolean
  withLegend?: boolean
  margins?: [number, number, number, number]
  hideBorder?: boolean
  className?: string
  additionalOptions?: Options
  /** Number of decimal places for Y-axis labels and tooltips (default: 2) */
  digits?: number
}

export function SimpleChart({
  unit,
  series,
  plotlinesY,
  statuses,
  height,
  flexGrow,
  noAnimation,
  borderRadius = 5,
  hideBorder = false,
  hideXAxis = false,
  hideYAxis = false,
  withLegend = false,
  margins,
  additionalOptions,
  className,
  digits = 2
}: SimpleChartProps) {
  const { theme, themeMode } = useChartTheme()

  const { options: baseOptions } = useHighchartsOptions()

  const options: Options = useMemo(() => {
    const yFormatter = (value?: number | string) => {
      if (unit === '%') {
        return formatters.percent
          .unit(unit)
          .digits(digits)
          .default('-')
          .max(100)
          .trailingZero(false)
          .of(Number(value))
      }
      return formatters.short
        .unit(unit)
        .digits(digits)
        .default('-')
        .trailingZero(false)
        .of(Number(value))
    }

    const tooltipFormatter = createTooltipFormatter({
      // Show more info in tooltip if chart has more than 1 line
      formatters:
        series.length > 1
          ? {
              y: point => `${point.series.name}: ${yFormatter(point.y)}`,
              showSeriesColor: true
            }
          : {
              y: point => yFormatter(point.y)
            },
      theme,
      themeMode
    })

    return mergeOptions(
      baseOptions,
      {
        plotOptions: {
          series: {
            animation: !noAnimation,
            lineWidth: 1,
            states: {
              hover: {
                lineWidthPlus: 0,
                halo: { size: 0 }
              }
            }
          },
          areaspline: {
            lineWidth: 1
          },
          area: {
            lineWidth: 1
          },
          spline: {
            lineWidth: 1
          }
        },
        chart: {
          borderRadius: borderRadius,
          plotBorderWidth: hideBorder ? 0 : 1,
          margin: margins ?? baseOptions.chart?.margin
        },
        yAxis: {
          labels: {
            formatter: function () {
              return yFormatter(this.value)
            }
          },
          plotLines: plotlinesY,
          visible: !hideYAxis
        },
        xAxis: {
          type: 'datetime',
          visible: !hideXAxis
        },
        tooltip: {
          formatter: tooltipFormatter
        },
        legend: {
          enabled: withLegend
        },
        series: series
      },
      additionalOptions ?? {}
    )
  }, [
    baseOptions,
    theme,
    themeMode,
    series,
    plotlinesY,
    hideBorder,
    unit,
    noAnimation,
    borderRadius,
    hideXAxis,
    margins,
    hideYAxis,
    withLegend,
    additionalOptions,
    digits
  ])

  return (
    <Chart
      className={className}
      options={options}
      statuses={statuses}
      height={height}
      borderRadius={borderRadius}
      unit={unit}
      flexGrow={flexGrow}
      hideBorder={hideBorder}
      hideYAxis={hideYAxis}
      hideXAxis={hideXAxis}
    />
  )
}

export default memo(SimpleChart)
